import { Link } from '@mui/material';
import L from 'leaflet';
import React from 'react';
import { useMap } from 'react-leaflet';

import { combineClassNamesConditional } from '../../../../Lib/Utils/combineClassNames';

import styles from '../leaflet.module.css';


interface ButtonControlProps {
  position: L.ControlPosition
  children?: React.ReactNode,
  prepend?: boolean,
  title?: string
  disabled?: boolean;
  onClick?: (map: L.Map) => void
}

const POSITION_CLASSES = {
  bottomleft: 'leaflet-bottom leaflet-left',
  bottomright: 'leaflet-bottom leaflet-right',
  topleft: 'leaflet-top leaflet-left',
  topright: 'leaflet-top leaflet-right',
}

const ButtonControl = (props: ButtonControlProps): JSX.Element => {

  const [portalRoot, setPortalRoot] = React.useState<any>(document.createElement('div'))
  const positionClass = ((props.position && POSITION_CLASSES[props.position]) || POSITION_CLASSES.topright)
  const controlContainerRef = React.createRef<HTMLDivElement>()
  const map = useMap();


  const className = combineClassNamesConditional([
    [styles.control_link, true], 
    [styles.control_link_disabled, !!props.disabled]]
  );

  const handleClick = () => {
    if (!props.disabled && !!props.onClick) {
      props.onClick(map);
    }
  };


  /**
   * Whenever the control container ref is created, 
   * Ensure the click / scroll propagation is removed
   * This way click/scroll events do not bubble down to the map
   */
  React.useEffect(() => {
    if (controlContainerRef.current !== null) {
      L.DomEvent.disableClickPropagation(controlContainerRef.current)
      L.DomEvent.disableScrollPropagation(controlContainerRef.current)
    }
  }, [controlContainerRef])

  /**
   * Whenever the position is changed, go ahead and get the container of the map and the first
   * instance of the position class in that map container
   * Fixes #17
   */
  React.useEffect(() => {
    const mapContainer = map.getContainer()
    const targetDiv = mapContainer.getElementsByClassName(positionClass)
    setPortalRoot(targetDiv[0])
  }, [map, positionClass])

  /**
   * Whenever the portal root is complete,
   * append or prepend the control container to the portal root
   */
  React.useEffect(() => {
    if (portalRoot !== null) {
      if (props.prepend !== undefined && props.prepend === true) {
        portalRoot.prepend(controlContainerRef.current)
      } else {
        portalRoot.append(controlContainerRef.current)
      }
    }
  }, [portalRoot, props.prepend, controlContainerRef])

  /**
   * Render
   */
  return (
    <div
      ref={controlContainerRef}
      className="leaflet-control leaflet-bar"
    >
      <Link title={props.title} onClick={!props.disabled ? handleClick : undefined} className={className}>
        {props.children}
      </Link>
    </div>
  )
}

export default ButtonControl;